import React from 'react';
import styled from 'styled-components';
import ActivePlanPreview, { PlanId } from './ActivePlanPreview';

type Props = {
    planLabel: string;
    planId: PlanId;
    /** Short line under the plan name, e.g. "$3,995/mo • Renews on Nov 3" */
    subText?: string;
    onCancel?: () => void;
    className?: string;
    cancelAt?: number | null;
    onResume?: () => void;
    canceling?: boolean;
    resuming?: boolean;
};

const Grid = styled.section`
  width: 100%;
  min-height: min(80vh, auto);
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    align-items: start;
    min-height: auto;
  }
`;

const Left = styled.div`
  padding: .75rem .5rem;
`;

const Label = styled.div`
  color: #cbd2e1;
  font-weight: 900;
  font-size: .95rem;
  margin-bottom: .35rem;
`;

const Big = styled.h2`
  margin: 0;
  line-height: 1.06;
  color: #e8eaed;
  font-weight: 900;
  font-size: clamp(2.2rem, 5.4vw, 3.6rem);
`;

const Sub = styled.div`
  margin-top: .5rem;
  color: #cbd2e1;
  font-weight: 700;
`;

const Right = styled.div`
  display: grid;
  justify-items: end;
  align-content: start;
  gap: .5rem;
`;

const CancelLink = styled.button`
  justify-self: center;
  appearance: none; border: 0; background: transparent;
  padding: .25rem 0;
  color: #ff5a5f;
  font-weight: 900;
  cursor: pointer;
//   text-decoration: underline;
  &:hover { opacity: .9; }
`;


const StatusPill = styled.span<{ $active?: boolean }>`
  margin: .25rem auto 0;
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  padding: .35rem .7rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: .85rem;
  color: ${({ $active }) => ($active ? '#052e1a' : '#3b2a00')};
  background: ${({ $active }) =>
        $active
            ? 'linear-gradient(180deg, #A7F3D0, #6EE7B7)'
            : 'linear-gradient(180deg, #FDE68A, #F59E0B)'};
  box-shadow: 0 6px 18px rgba(0,0,0,.18);
  border: 1px solid rgba(0,0,0,.2);
`;


// ADD near other styled components
const DangerNote = styled.div`
  margin-top: .45rem;
  color: #ff6b6b;
  font-weight: 900;
`;

const ResumeLink = styled.button`
  justify-self: center;
  appearance: none; border: 0; background: transparent;
  padding: .25rem 0;
  color: #10b981;
  font-weight: 900;
  cursor: pointer;
  &:hover { opacity: .9; }
`;


export default function ActiveSubscriptionHero({ planLabel, planId, subText, onCancel, className, cancelAt, onResume, resuming, canceling }: Props) {


    // ADD at the start of the component body
    const isScheduledCancel = typeof cancelAt === 'number' && cancelAt > 0;
    const cancelDateStr = isScheduledCancel
        ? new Date(cancelAt! * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
        : null;



    return (

        <>
            <StatusPill $active={!isScheduledCancel}>
                {isScheduledCancel ? 'Cancellation scheduled' : 'Active subscription'}
            </StatusPill>
            <Grid className={className} aria-label="Current subscription">

                <Left>
                    <Label>Your current plan</Label>
                    <Big>{planLabel}</Big>
                    {subText ? <Sub>{subText}</Sub> : null}
                    {cancelDateStr ? <DangerNote>Ends on {cancelDateStr}</DangerNote> : null}
                </Left>

                <Right>
                    <ActivePlanPreview planId={planId} />
                    {isScheduledCancel ? (
                        <ResumeLink type="button" onClick={onResume} disabled={!!resuming}>
                            {resuming ? 'Resuming…' : 'Resume Subscription'}
                        </ResumeLink>
                    ) : (
                        <CancelLink type="button" onClick={onCancel} disabled={!!canceling}>
                            {canceling ? 'Canceling…' : 'Cancel Subscription'}
                        </CancelLink>
                    )}
                </Right>
            </Grid>
        </>
    );
}
